/**
 * Pricing Component Styles
 */

export const PRICING_STYLES = `
  .ks-pricing {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: var(--ks-spacing, 1.25rem);
  }

  /* Header: frequency toggle + currency selector */
  .ks-pricing__header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    margin-bottom: 2rem;
    flex-wrap: wrap;
  }

  /* Frequency toggle group */
  .ks-pricing__toggle {
    display: inline-flex;
    background: var(--ks-surface, #f3f4f6);
    border-radius: var(--ks-radius, 0.75rem);
    padding: 4px;
    gap: 2px;
  }

  .ks-pricing__toggle-btn {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: calc(var(--ks-radius, 0.75rem) - 2px);
    background: transparent;
    color: var(--ks-text-muted, #6B7280);
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all var(--ks-transition, 300ms) ease;
    font-family: inherit;
    white-space: nowrap;
  }

  .ks-pricing__toggle-btn:hover {
    color: var(--ks-text, #111827);
  }

  .ks-pricing__toggle-btn--active {
    background: var(--ks-primary, #6366F1);
    color: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .ks-pricing__toggle-btn--active:hover {
    color: white;
  }

  /* Currency selector */
  .ks-pricing__currency {
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--ks-border, #E5E7EB);
    border-radius: var(--ks-radius, 0.75rem);
    background: var(--ks-surface, #F9FAFB);
    color: var(--ks-text, #111827);
    font-size: 0.875rem;
    font-family: inherit;
    cursor: pointer;
    transition: border-color var(--ks-transition, 300ms) ease;
  }

  .ks-pricing__currency:hover {
    border-color: var(--ks-primary, #6366F1);
  }

  .ks-pricing__currency:focus {
    outline: none;
    border-color: var(--ks-primary, #6366F1);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
  }

  /* Pricing grid */
  .ks-pricing__grid {
    display: grid;
    grid-template-columns: repeat(var(--ks-columns, 3), minmax(0, 1fr));
    gap: var(--ks-spacing, 1.25rem);
    align-items: stretch;
  }

  @media (max-width: 1024px) {
    .ks-pricing__grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 640px) {
    .ks-pricing__grid {
      grid-template-columns: 1fr;
    }

    .ks-pricing__header {
      flex-direction: column;
    }
  }

  /* Pricing card */
  .ks-pricing-card {
    display: flex;
    flex-direction: column;
    border-radius: var(--ks-radius, 0.75rem);
    padding: 1.5rem;
    position: relative;
    transition: all var(--ks-transition, 300ms) ease;
    border: 2px solid transparent;
  }

  .ks-pricing-card:hover {
    transform: translateY(-2px);
  }

  /* Highlighted/popular plan */
  .ks-pricing-card--highlighted {
    border-color: var(--ks-primary, #6366F1);
    transform: scale(1.02);
  }

  .ks-pricing-card--highlighted:hover {
    transform: scale(1.02) translateY(-2px);
  }

  .ks-pricing-card__badge {
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    background: var(--ks-primary, #6366F1);
    color: white;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.25rem 1rem;
    border-radius: 9999px;
    white-space: nowrap;
  }

  /* Plan name */
  .ks-pricing-card__name {
    font-size: 1.125rem;
    font-weight: var(--ks-heading-weight, 600);
    color: var(--ks-text, #111827);
    margin: 0 0 0.25rem 0;
  }

  .ks-pricing-card__description {
    font-size: 0.875rem;
    color: var(--ks-text-muted, #6B7280);
    margin: 0 0 1rem 0;
    line-height: 1.4;
  }

  /* Price display */
  .ks-pricing-card__price {
    margin-bottom: 1.5rem;
  }

  .ks-pricing-card__amount {
    font-size: 2.25rem;
    font-weight: 700;
    color: var(--ks-text, #111827);
    line-height: 1;
  }

  .ks-pricing-card__frequency {
    font-size: 0.875rem;
    color: var(--ks-text-muted, #6B7280);
    margin-left: 0.25rem;
  }

  .ks-pricing-card__original-price {
    font-size: 0.875rem;
    color: var(--ks-text-muted, #6B7280);
    text-decoration: line-through;
    margin-top: 0.25rem;
  }

  .ks-pricing-card__discount {
    display: inline-block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #059669;
    background: #ecfdf5;
    padding: 0.125rem 0.5rem;
    border-radius: 9999px;
    margin-left: 0.5rem;
  }

  :host([data-ks-mode="dark"]) .ks-pricing-card__discount {
    background: rgba(5, 150, 105, 0.15);
  }

  /* Divider */
  .ks-pricing-card__divider {
    border: none;
    border-top: 1px solid var(--ks-border, #E5E7EB);
    margin: 0 0 1.5rem 0;
  }

  /* Features list */
  .ks-pricing-card__features {
    list-style: none;
    padding: 0;
    margin: 0 0 1.5rem 0;
    flex: 1;
  }

  .ks-pricing-card__feature {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.375rem 0;
    font-size: 0.875rem;
    color: var(--ks-text, #111827);
    line-height: 1.4;
  }

  .ks-pricing-card__feature-icon {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin-top: 1px;
    color: var(--ks-primary, #6366F1);
  }

  /* Benefits section */
  .ks-pricing-card__benefits {
    margin-top: auto;
    padding-top: 0.75rem;
    border-top: 1px solid var(--ks-border, #E5E7EB);
    margin-bottom: 1rem;
  }

  .ks-pricing-card__benefit {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    color: var(--ks-text-muted, #6B7280);
    padding: 0.25rem 0;
  }

  .ks-pricing-card__benefit-icon {
    color: #059669;
    font-size: 0.75rem;
  }

  /* CTA button */
  .ks-pricing-card__cta {
    width: 100%;
    margin-top: auto;
    padding: 0.75rem 1.5rem;
    font-size: 0.9375rem;
    font-weight: 600;
    border: 2px solid transparent;
    border-radius: var(--ks-radius, 0.5rem);
    cursor: pointer;
    transition: all var(--ks-transition, 300ms) ease;
    font-family: inherit;
    text-align: center;
    text-decoration: none;
    display: inline-block;
  }

  .ks-pricing-card__cta--primary {
    background: var(--ks-primary, #6366F1);
    color: white;
    border-color: var(--ks-primary, #6366F1);
  }

  .ks-pricing-card__cta--primary:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  .ks-pricing-card__cta--secondary {
    background: transparent;
    color: var(--ks-primary, #6366F1);
    border-color: var(--ks-primary, #6366F1);
  }

  .ks-pricing-card__cta--secondary:hover:not(:disabled) {
    background: var(--ks-primary, #6366F1);
    color: white;
  }

  .ks-pricing-card__cta:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .ks-pricing-card__cta .ks-spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border-width: 2px;
    border-top-color: currentColor;
    vertical-align: middle;
    margin-right: 0.5rem;
  }

  /* Free plan card */
  .ks-pricing-card--free .ks-pricing-card__amount {
    font-size: 2.25rem;
  }

  /* Trial notice */
  .ks-pricing-card__trial {
    font-size: 0.75rem;
    color: #059669;
    font-weight: 500;
    margin-top: 0.25rem;
  }

  /* Skeleton loading */
  .ks-pricing-card--skeleton {
    min-height: 400px;
  }

  .ks-pricing-card--skeleton .ks-skeleton-block {
    border-radius: 4px;
    margin-bottom: 0.75rem;
  }

  .ks-skeleton-block--title {
    height: 20px;
    width: 60%;
  }

  .ks-skeleton-block--price {
    height: 40px;
    width: 45%;
    margin-top: 0.5rem;
  }

  .ks-skeleton-block--feature {
    height: 14px;
    width: 80%;
  }

  .ks-skeleton-block--feature:nth-child(even) {
    width: 65%;
  }

  .ks-skeleton-block--button {
    height: 44px;
    width: 100%;
    margin-top: auto;
  }

  /* Dark mode overrides */
  :host([data-ks-mode="dark"]) .ks-pricing__toggle {
    background: rgba(255, 255, 255, 0.05);
  }

  :host([data-ks-mode="dark"]) .ks-pricing__toggle-btn {
    color: var(--ks-text-muted, #94a3b8);
  }

  :host([data-ks-mode="dark"]) .ks-pricing__toggle-btn:hover {
    color: var(--ks-text, #f8fafc);
  }

  :host([data-ks-mode="dark"]) .ks-pricing__currency {
    background: var(--ks-surface, #1e293b);
    color: var(--ks-text, #f8fafc);
    border-color: var(--ks-border, #334155);
  }

  :host([data-ks-mode="dark"]) .ks-pricing-card__trial {
    color: #34d399;
  }

  :host([data-ks-mode="dark"]) .ks-pricing-card__benefit-icon {
    color: #34d399;
  }
`;
